// mission-store.ts
//
// Ghép journal (sự thật) + reducer (thuần) + projection (cache bền):
//
//   open(controlRoot) → scan journal
//     clean        → replay, ghi/nạp projection
//     partial_tail → recovery-reconciler (manifest-driven) rồi replay
//     corrupt      → status RECOVERING, commit BỊ TỪ CHỐI (§3.4: không skip record hỏng)
//
//   commit(events) → [che known-values ở field text] → append (fsync) → reduce
//                    → writeFileDurable projection
//
// Phase 04a B11: trước append + reducer, nếu registry known-values của mission
// (mission-secrets-test-env-loader.ts, đọc lazy theo control root) khác rỗng
// ⇒ che CHỈ field text tự do theo allowlist (journal-delta-known-secret-text-
// fields.ts). Registry rỗng ⇒ delta nguyên văn, byte-identical v1.
//
// Projection mang `last_event_seq`. Projection VƯỢT journal = bằng chứng mất dữ liệu
// ⇒ RECOVERING (không được coi là "cache cũ, rebuild là xong").
//
// Logic quyết định READY/RECOVERING lúc `open()` (scan/recovery/so projection) đã
// tách sang `mission-store-open-recovery.ts` (phase 10, lô A) — con trỏ vào đó.

import { join } from "node:path";
import { ensureDir } from "../filesystem-helpers.js";
import { type DurableFsOps, realDurableFsOps } from "./durable-file-writer.js";
import { EventJournal, type JournalEventInput } from "./event-journal.js";
import { redactJournalDeltaTextFields } from "./journal-delta-known-secret-text-fields.js";
import { writeProjection } from "./mission-projection-io.js";
import { knownValuesForControlRoot } from "./mission-secrets-test-env-loader.js";
import {
  listOpenIntents,
  type MissionDelta,
  type MissionState,
  type OperationIntentRecord,
  reduceMission,
} from "./mission-state-reducer.js";
import { resolveMissionStoreOpenOutcome } from "./mission-store-open-recovery.js";
import type { RecoveryDeps } from "./recovery-reconciler.js";

export type MissionStoreStatus = "READY" | "RECOVERING";

export interface MissionStoreOpenOptions {
  ops?: DurableFsOps;
  recovery?: RecoveryDeps;
  appendQueueTimeoutMs?: number;
}

export interface MissionCommitInput {
  event_id: string;
  operation_id?: string;
  delta: MissionDelta;
}

const PROJECTION_FILE = "mission.json";
/** Tên file journal trong control root — reader ngoài store (vd decision-answered-journal-event.ts) dùng chung hằng này. */
export const MISSION_JOURNAL_FILE_NAME = "journal.jsonl";
const JOURNAL_FILE = MISSION_JOURNAL_FILE_NAME;

export class MissionStore {
  private constructor(
    readonly controlRoot: string,
    private readonly journal: EventJournal,
    private mission: MissionState,
    readonly status: MissionStoreStatus,
    readonly recoveringReason: string | undefined,
    readonly replayedFrom: number,
    private readonly ops: DurableFsOps,
  ) {}

  static async open(
    controlRoot: string,
    options: MissionStoreOpenOptions = {},
  ): Promise<MissionStore> {
    const ops = options.ops ?? realDurableFsOps;
    await ensureDir(controlRoot);
    const journalPath = join(controlRoot, JOURNAL_FILE);
    const { journal, scan } = await EventJournal.open(journalPath, {
      ops,
      appendQueueTimeoutMs: options.appendQueueTimeoutMs,
    });

    try {
      const outcome = await resolveMissionStoreOpenOutcome(
        journalPath,
        join(controlRoot, PROJECTION_FILE),
        journal,
        scan,
        options.recovery,
      );

      if (outcome.status === "RECOVERING") {
        return new MissionStore(
          controlRoot,
          journal,
          outcome.state,
          "RECOVERING",
          outcome.reason,
          0,
          ops,
        );
      }

      const store = new MissionStore(
        controlRoot,
        journal,
        outcome.state,
        "READY",
        undefined,
        outcome.replayedFrom,
        ops,
      );
      const baseLastEventSeq = outcome.replayedFrom - 1;
      if (outcome.state.last_event_seq !== baseLastEventSeq || !outcome.hadExistingProjection) {
        await store.persistProjection();
      }
      return store;
    } catch (err) {
      await journal.close();
      throw err;
    }
  }

  get state(): MissionState {
    return this.mission;
  }

  openIntents(): OperationIntentRecord[] {
    return listOpenIntents(this.mission);
  }

  /**
   * Ghi event rồi cập nhật projection. Resolve = journal ĐÃ fsync và projection đã
   * ghi bền ⇒ caller mới được ack/side effect.
   */
  async commit(events: MissionCommitInput[]): Promise<number> {
    if (this.status === "RECOVERING") {
      throw new Error(
        `MissionStore đang ở trạng thái RECOVERING — commit bị từ chối. ${this.recoveringReason ?? ""}`,
      );
    }
    const secretValues = await knownValuesForControlRoot(this.controlRoot);
    const inputs: JournalEventInput[] = events.map((e) => ({
      event_id: e.event_id,
      operation_id: e.operation_id,
      delta: redactJournalDeltaTextFields(e.delta, secretValues),
    }));
    const appended = await this.journal.append(inputs);
    let next = this.mission;
    for (const record of appended) {
      next = reduceMission(next, record.delta as MissionDelta, record.seq);
    }
    this.mission = next;
    await this.persistProjection();
    return next.last_event_seq;
  }

  private async persistProjection(): Promise<void> {
    await writeProjection(join(this.controlRoot, PROJECTION_FILE), this.mission, this.ops);
  }

  async close(): Promise<void> {
    await this.journal.close();
  }
}
